import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertCircle, CheckSquare, Circle, RectangleHorizontal, ShoppingBag, Square, Star, X } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useShop } from "@/context/ShopContext";
import { AppButton, Chip, EmptyState, Footer, SectionTitle, Stepper } from "@/components/AppUI";
import { SIZE_LABELS, euro, fileUrl, ingredientsOf, papi, priceFor, promoOf, sizesOf } from "@/lib/pizzeria";

export const SIZE_ICONS = { rotonda: Circle, teglia: Square, mezza: RectangleHorizontal };

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function ProductPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { add } = useCart();
  const { products, settings, deals: featured } = useShop();
  const [p, setP] = useState(null);
  const [missing, setMissing] = useState(false);
  const [size, setSize] = useState(null);
  const [extras, setExtras] = useState([]);
  const [removed, setRemoved] = useState([]);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    const local = (products || []).find((x) => x.id === id);
    if (local) {
      setP(local);
    } else if (products) {
      papi.get(`/products/${id}`).then(({ data }) => setP(data)).catch(() => setMissing(true));
    }
  }, [id, products]);

  useEffect(() => {
    if (p) {
      setSize(sizesOf(p)[0] || null);
    }
  }, [p]);

  if (missing) return <EmptyState icon={AlertCircle} title="Prodotto non trovato"><AppButton title="Torna al menù" onClick={() => nav("/")} testId="product-retry" /></EmptyState>;
  if (!p) return null;

  const sizes = sizesOf(p);
  const pct = promoOf(p, featured);
  const extraList = p.sized ? (settings?.extras || []).filter((x) => x.available !== false) : [];
  const ingredients = p.sized ? ingredientsOf(p) : [];
  const chosen = extraList.filter((x) => extras.includes(x.id));
  const unit = priceFor(p, size, featured) + chosen.reduce((a, x) => a + x.price, 0);
  const cartUnit = priceFor(p, size, { featured: featured?.featured }) + chosen.reduce((a, x) => a + x.price, 0);
  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const addToCart = () => {
    add({ product_id: p.id, name: p.name, image: p.image, size, quantity: qty, unit_price: cartUnit, extras, extra_names: chosen.map((x) => x.name), removed });
    toast.success(`${qty > 1 ? `${qty}× ` : ""}${p.name} aggiunto al carrello`);
    nav(-1);
  };

  return (
    <div className="flex-1 flex flex-col" data-testid="product-page">
      <div className="flex-1">
        <div className="relative">
          {p.image ? <img src={fileUrl(p.image)} alt={p.name} className="w-full h-[260px] object-cover" /> : <div className="h-[120px]" style={{ background: "var(--surface-3)" }} />}
          <button onClick={() => nav(-1)} className="absolute top-4 right-4 h-10 w-10 rounded-full grid place-items-center" style={{ background: "var(--surface-2)" }} data-testid="product-close-button"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <h1 className="text-2xl font-medium" data-testid="product-name">{p.name}</h1>
            {pct > 0 && <p className="mt-2 flex items-center gap-2 rounded-xl p-3 text-sm font-medium" style={{ background: "var(--brand-3)", color: "var(--on-brand-3)" }} data-testid="product-promo-badge"><Star className="h-4 w-4 fill-current" />In promozione: -{pct}%</p>}
            {p.description && <p className="mt-1 text-base leading-[22px]" style={{ color: "var(--muted-c)" }}>{p.description}</p>}
          </div>
          {sizes.length > 0 && (
            <div className="space-y-3">
              <SectionTitle>Scegli il formato</SectionTitle>
              <div className="flex gap-3">
                {sizes.map((s) => {
                  const Icon = SIZE_ICONS[s];
                  const on = size === s;
                  return (
                    <button key={s} onClick={() => setSize(s)} data-testid={`product-size-${s}`} className="press flex-1 p-3 rounded-[20px] text-left space-y-1"
                      style={{ border: `1.5px solid ${on ? "var(--brand)" : "var(--border-c)"}`, background: on ? "var(--brand-3)" : "var(--surface-2)" }}>
                      <Icon className="h-6 w-6" style={{ color: on ? "var(--brand)" : "var(--muted-c)" }} />
                      <p className="text-base font-medium mt-1 truncate">{SIZE_LABELS[s]}</p>
                      <p className="text-base font-medium" style={{ color: on ? "var(--brand)" : "var(--on-surface)" }}>{euro(priceFor(p, s, featured))}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {ingredients.length > 0 && (
            <div className="space-y-2" data-testid="product-remove-section">
              <SectionTitle>Vuoi togliere qualcosa?</SectionTitle>
              <div className="flex flex-wrap gap-2">
                {ingredients.map((i) => <Chip key={i} on={removed.includes(i)} strike={removed.includes(i)} onClick={() => toggle(removed, setRemoved, i)} testId={`product-remove-${slug(i)}`}>{removed.includes(i) ? `senza ${i}` : i}</Chip>)}
              </div>
            </div>
          )}
          {extraList.length > 0 && (
            <div>
              <SectionTitle>Aggiungi ingredienti</SectionTitle>
              <div className="rounded-xl px-3" style={{ background: "var(--surface-2)", border: "1px solid var(--border-c)" }}>
                {extraList.map((x, i) => {
                  const on = extras.includes(x.id);
                  const Icon = on ? CheckSquare : Square;
                  return (
                    <button key={x.id} onClick={() => toggle(extras, setExtras, x.id)} data-testid={`product-extra-${x.id}`} className="w-full flex items-center gap-3 min-h-[48px] text-left" style={i ? { borderTop: "1px solid var(--border-c)" } : {}}>
                      <Icon className="h-[22px] w-[22px]" style={{ color: on ? "var(--brand)" : "var(--muted-c)" }} />
                      <span className="flex-1 text-base">{x.name}</span>
                      <span className="text-sm font-medium" style={{ color: "var(--muted-c)" }}>+ {euro(x.price)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between"><SectionTitle>Quantità</SectionTitle><Stepper value={qty} onChange={(v) => setQty(Math.max(1, v))} testIdPrefix="product-qty" /></div>
        </div>
      </div>
      <Footer><AppButton title={`Aggiungi al carrello · ${euro(unit * qty)}`} icon={ShoppingBag} onClick={addToCart} testId="add-to-cart-button" className="w-full" /></Footer>
    </div>
  );
}
